import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Download, Filter, Megaphone, TrendingUp, Users, UsersRound, Wallet } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import {
  ATTRIBUTION_MODEL_LABEL,
  ATTRIBUTION_MODELS,
  EXPORT_ENTITY_LABEL,
  EXPORT_ENTITIES,
} from "@rn/contracts";
import { PageBody, PageHeader } from "@/components/crm/AppShell";
import { CampaignResultsPanel } from "@/components/crm/CampaignResultsPanel";
import { ConversionsPanel } from "@/components/crm/ConversionsPanel";
import { DailySpendChart } from "@/components/crm/DailySpendChart";
import { LeadSourcesPanel } from "@/components/crm/LeadSourcesPanel";
import { PeriodPicker } from "@/components/crm/PeriodPicker";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { EmptyState } from "@/components/ui/empty-state";
import { Select } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Stat } from "@/components/ui/stat";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, tokens } from "@/lib/api";
import { formatCurrency } from "@/lib/crm";
import { usePeriod } from "@/lib/period";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/relatorios")({
  component: RelatoriosPage,
});

type Overview = {
  period: {
    from: string;
    to: string;
    days: number;
    fromDay: string;
    toDay: string;
    preset: string;
  };
  leads: {
    total: number;
    respondidos: number;
    qualificados: number;
    convertidos: number;
    tempoMedioRespostaSegundos: number | null;
  };
  agenda: {
    agendados: number;
    atendidos: number;
    faltas: number;
    taxaComparecimento: number | null;
  };
  receita: {
    vendas: number;
    total: number;
    custo: number;
    margem: number;
    aReceber: number;
    ticketMedio: number | null;
  };
  midia: {
    investimento: number;
    impressoes: number;
    cliques: number;
    ctr: number | null;
    cpl: number | null;
    cac: number | null;
    roas: number | null;
    roi: number | null;
  };
  analyticsEnabled: boolean;
};

type Funnel = {
  stages: Array<{
    stageId: string;
    name: string;
    count: number;
    isWon: boolean;
    conversionFromPrevious: number | null;
  }>;
  lost: number;
};

type Attribution = {
  available: boolean;
  reason?: string;
  model: string;
  rows: Array<{
    platform: string;
    campaignExternalId: string | null;
    revenue: number;
    sales: number;
  }>;
};

type Team = {
  members: Array<{
    userId: string;
    name: string | null;
    leads: number;
    respondidos: number;
    tempoMedioRespostaSegundos: number | null;
    convertidos: number;
    taxaConversao: number | null;
  }>;
};

const pct = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : `${(v * 100).toFixed(1)}%`;

const num = (v: number | null | undefined, casas = 2) =>
  v === null || v === undefined ? "—" : v.toFixed(casas);

const dinheiro = (v: number | null | undefined) =>
  v === null || v === undefined ? "—" : formatCurrency(v);

/** Segundos em "12 min" ou "1h 05". Em segundos crus ninguém lê. */
const duracao = (s: number | null | undefined) => {
  if (s === null || s === undefined) return "—";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)} min`;
  return `${Math.floor(s / 3600)}h ${String(Math.round((s % 3600) / 60)).padStart(2, "0")}`;
};

/** Cabeçalho de tabela padrão (§5 do guia), para as três tabelas desta tela. */
const THEAD =
  "bg-muted/60 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground";

/**
 * Relatórios.
 *
 * O período é sempre calculado no fuso da clínica, no servidor. Fazer a conta
 * no navegador daria respostas diferentes para quem abre a tela viajando — e
 * nenhuma delas seria a do calendário de quem vendeu.
 */
function RelatoriosPage() {
  // O período é o global, e cada aba o lê sozinha (`usePeriod`) em vez de
  // recebê-lo por prop: é o mesmo do painel e do marketing, e uma aba nova não
  // tem como esquecer de repassá-lo e mostrar outro intervalo sem avisar.
  const { query, key } = usePeriod();
  const [model, setModel] = useState<(typeof ATTRIBUTION_MODELS)[number]>("last_touch");

  const overview = useQuery({
    queryKey: ["analytics", "overview", key],
    queryFn: () => api.get<Overview>("/analytics/overview", query),
  });

  const funnel = useQuery({
    queryKey: ["analytics", "funnel", key],
    queryFn: () => api.get<Funnel>("/analytics/funnel", query),
  });

  const attribution = useQuery({
    queryKey: ["analytics", "attribution", key, model],
    queryFn: () => api.get<Attribution>("/analytics/attribution", { ...query, model }),
  });

  const team = useQuery({
    queryKey: ["analytics", "team", key],
    queryFn: () => api.get<Team>("/analytics/team", query),
  });

  const o = overview.data;
  const roas = o?.midia.roas ?? null;
  const maiorEtapa = Math.max(1, ...(funnel.data?.stages ?? []).map((s) => s.count));

  return (
    <>
      <PageHeader
        title="Relatórios"
        description="Quanto entrou, quanto custou e onde o funil trava"
        action={<PeriodPicker />}
      />

      <PageBody className="space-y-5">
        {/* ── Números do topo ─────────────────────────────────────────────── */}
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
          <Stat
            label="Leads"
            value={(o?.leads.total ?? 0).toLocaleString("pt-BR")}
            icon={Users}
            tone="primary"
            loading={overview.isPending}
            hint={`${o?.leads.respondidos ?? 0} respondidos · resposta em ${duracao(o?.leads.tempoMedioRespostaSegundos)}`}
          />
          <Stat
            label="Receita paga"
            value={formatCurrency(o?.receita.total ?? 0)}
            icon={Wallet}
            tone="success"
            loading={overview.isPending}
            hint={`${o?.receita.vendas ?? 0} venda(s) · ticket ${dinheiro(o?.receita.ticketMedio)}`}
          />
          <Stat
            label="Investimento"
            value={formatCurrency(o?.midia.investimento ?? 0)}
            icon={Megaphone}
            loading={overview.isPending}
            hint={`CPL ${dinheiro(o?.midia.cpl)} · CAC ${dinheiro(o?.midia.cac)}`}
          />
          <Stat
            label="ROAS"
            value={roas === null ? "—" : `${num(roas)}x`}
            icon={TrendingUp}
            tone={roas !== null && roas < 1 ? "danger" : "default"}
            loading={overview.isPending}
            // ROAS nulo é "não sei", e não "zero": sem investimento não há
            // divisão possível, e mostrar 0,00x sugere que a mídia não
            // devolveu nada.
            hint={
              roas === null
                ? "Sem investimento no período"
                : `Margem ${formatCurrency(o?.receita.margem ?? 0)}`
            }
          />
        </div>

        <Tabs defaultValue="diario">
          <TabsList>
            <TabsTrigger value="diario">Dia a dia</TabsTrigger>
            <TabsTrigger value="funil">Funil</TabsTrigger>
            <TabsTrigger value="campanhas">Campanhas</TabsTrigger>
            <TabsTrigger value="atribuicao">Atribuição</TabsTrigger>
            <TabsTrigger value="origem">Origem</TabsTrigger>
            <TabsTrigger value="equipe">Equipe</TabsTrigger>
            <TabsTrigger value="conversoes">Conversões</TabsTrigger>
            <TabsTrigger value="exportar">Exportar</TabsTrigger>
          </TabsList>

          {/* ── Dia a dia ─────────────────────────────────────────────────── */}
          <TabsContent value="diario">
            <DailySpendChart />
          </TabsContent>

          {/* ── Origem ────────────────────────────────────────────────────── */}
          <TabsContent value="origem">
            <LeadSourcesPanel />
          </TabsContent>

          {/* ── Funil ─────────────────────────────────────────────────────── */}
          <TabsContent value="funil">
            <Card>
              <CardHeader>
                <div className="min-w-0">
                  <CardTitle>Funil de vendas</CardTitle>
                  <CardDescription>
                    Quantos negócios chegaram a cada etapa. A taxa é em relação à etapa anterior — é
                    ela que mostra onde o processo trava, não a fração do topo.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                {funnel.isPending ? (
                  <div className="space-y-3">
                    {Array.from({ length: 5 }, (_, i) => (
                      <Skeleton key={i} className="h-7 w-full" />
                    ))}
                  </div>
                ) : (funnel.data?.stages.length ?? 0) === 0 ? (
                  <EmptyState
                    compact
                    icon={Filter}
                    title="Nenhuma etapa configurada"
                    description="Crie o funil em Configurações › Funis para o relatório aparecer aqui."
                  />
                ) : (
                  <ol className="space-y-3">
                    {funnel.data?.stages.map((s) => (
                      <li key={s.stageId} className="flex items-center gap-3">
                        <span className="w-32 shrink-0 truncate text-sm sm:w-40" title={s.name}>
                          {s.name}
                        </span>
                        <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-muted">
                          <div
                            className={cn(
                              "h-full rounded-full transition-[width] duration-300",
                              s.isWon ? "bg-success" : "bg-primary",
                            )}
                            style={{ width: `${Math.max(2, (s.count / maiorEtapa) * 100)}%` }}
                          />
                        </div>
                        <span className="kpi w-10 shrink-0 text-right text-sm font-semibold">
                          {s.count}
                        </span>
                        <span className="w-16 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
                          {s.conversionFromPrevious === null ? "—" : pct(s.conversionFromPrevious)}
                        </span>
                      </li>
                    ))}
                  </ol>
                )}
              </CardContent>
              {funnel.data && (
                <CardFooter>{funnel.data.lost} negócio(s) perdido(s) no período.</CardFooter>
              )}
            </Card>
          </TabsContent>

          {/* ── Campanhas ─────────────────────────────────────────────────── */}
          <TabsContent value="campanhas">
            <CampaignResultsPanel />
          </TabsContent>

          {/* ── Atribuição ────────────────────────────────────────────────── */}
          <TabsContent value="atribuicao">
            <Card>
              <CardHeader>
                <div className="min-w-0">
                  <CardTitle>Receita atribuída</CardTitle>
                  <CardDescription>
                    Quanto da receita paga cada campanha recebe, pelo modelo escolhido.
                  </CardDescription>
                </div>
                <Select
                  value={model}
                  onChange={(e) => setModel(e.target.value as typeof model)}
                  aria-label="Modelo de atribuição"
                  className="w-52 [&>select]:h-8 [&>select]:text-xs"
                >
                  {ATTRIBUTION_MODELS.map((m) => (
                    <option key={m} value={m}>
                      {ATTRIBUTION_MODEL_LABEL[m]}
                    </option>
                  ))}
                </Select>
              </CardHeader>
              <CardContent className="px-0 pb-0 pt-3">
                {attribution.isPending ? (
                  <div className="space-y-2 px-5 pb-5">
                    {Array.from({ length: 4 }, (_, i) => (
                      <Skeleton key={i} className="h-9 w-full" />
                    ))}
                  </div>
                ) : !attribution.data?.available ? (
                  <EmptyState
                    compact
                    icon={Megaphone}
                    title="Atribuição indisponível"
                    description={attribution.data?.reason}
                    className="pb-8"
                  />
                ) : attribution.data.rows.length === 0 ? (
                  <EmptyState
                    compact
                    icon={Megaphone}
                    title="Nenhuma receita atribuída no período"
                    description="Vendas de quem chegou sem toque de campanha — indicação, porta de rua — não entram aqui de propósito."
                    className="pb-8"
                  />
                ) : (
                  <div className="overflow-x-auto rounded-b-xl">
                    <table className="w-full min-w-[32rem] text-sm">
                      <thead className={THEAD}>
                        <tr>
                          <th className="px-5 py-2.5 text-left">Plataforma</th>
                          <th className="px-4 py-2.5 text-left">Campanha</th>
                          <th className="px-4 py-2.5 text-right">Vendas</th>
                          <th className="px-5 py-2.5 text-right">Receita atribuída</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {attribution.data.rows.map((r, i) => (
                          <tr
                            key={`${r.platform}-${i}`}
                            className="transition-colors hover:bg-muted/40"
                          >
                            <td className="px-5 py-2.5">
                              <Badge variant="muted">{r.platform}</Badge>
                            </td>
                            <td className="px-4 py-2.5 font-medium">
                              {r.campaignExternalId ?? "—"}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{r.sales}</td>
                            <td className="px-5 py-2.5 text-right tabular-nums">
                              {formatCurrency(r.revenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Equipe ────────────────────────────────────────────────────── */}
          <TabsContent value="equipe">
            <Card>
              <CardHeader>
                <div className="min-w-0">
                  <CardTitle>Equipe</CardTitle>
                  <CardDescription>
                    Leads por responsável, quantos foram respondidos, em quanto tempo e quantos
                    viraram venda.
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0 pt-3">
                {team.isPending ? (
                  <div className="space-y-2 px-5 pb-5">
                    {Array.from({ length: 4 }, (_, i) => (
                      <Skeleton key={i} className="h-10 w-full" />
                    ))}
                  </div>
                ) : team.data?.members.length === 0 ? (
                  <EmptyState
                    compact
                    icon={UsersRound}
                    title="Nenhum lead com responsável no período"
                    description="Atribua os leads — na ficha ou pela distribuição automática — para comparar o atendimento."
                    className="pb-8"
                  />
                ) : (
                  <div className="overflow-x-auto rounded-b-xl">
                    <table className="w-full min-w-[40rem] text-sm">
                      <thead className={THEAD}>
                        <tr>
                          <th className="px-5 py-2.5 text-left">Atendente</th>
                          <th className="px-4 py-2.5 text-right">Leads</th>
                          <th className="px-4 py-2.5 text-right">Respondidos</th>
                          <th className="px-4 py-2.5 text-right">Tempo médio</th>
                          <th className="px-4 py-2.5 text-right">Convertidos</th>
                          <th className="px-5 py-2.5 text-right">Conversão</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {team.data?.members.map((m) => (
                          <tr key={m.userId} className="transition-colors hover:bg-muted/40">
                            <td className="px-5 py-2.5">
                              <span className="flex items-center gap-2.5">
                                <Avatar name={m.name} seed={m.userId} size="sm" />
                                <span className="truncate font-medium">{m.name ?? "Sem nome"}</span>
                              </span>
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{m.leads}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{m.respondidos}</td>
                            <td className="px-4 py-2.5 text-right tabular-nums">
                              {duracao(m.tempoMedioRespostaSegundos)}
                            </td>
                            <td className="px-4 py-2.5 text-right tabular-nums">{m.convertidos}</td>
                            <td className="px-5 py-2.5 text-right font-medium tabular-nums">
                              {pct(m.taxaConversao)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Conversões ────────────────────────────────────────────────── */}
          <TabsContent value="conversoes">
            <ConversionsPanel />
          </TabsContent>

          {/* ── Exportar ──────────────────────────────────────────────────── */}
          <TabsContent value="exportar">
            <Exportacao />
          </TabsContent>
        </Tabs>
      </PageBody>
    </>
  );
}

/**
 * Download da planilha.
 *
 * Com `fetch` e blob, e não com um `<a href>`: o endpoint exige o token no
 * cabeçalho, e um link simples baixaria um 401 em vez do arquivo.
 */
function Exportacao() {
  const { query } = usePeriod();
  const [entity, setEntity] = useState<(typeof EXPORT_ENTITIES)[number]>("leads");
  const [format, setFormat] = useState<"csv" | "xlsx">("xlsx");
  const [baixando, setBaixando] = useState(false);

  const baixar = async () => {
    setBaixando(true);
    try {
      const base = import.meta.env["VITE_API_URL"] ?? "/api";
      // O mesmo período das outras abas: a planilha tem que bater com os
      // números da tela, senão a primeira conferência no Excel já desmente o
      // relatório. `URLSearchParams` codifica cada valor.
      const params = new URLSearchParams({ entity, format, preset: query.preset });
      if (query.from) params.set("from", query.from);
      if (query.to) params.set("to", query.to);
      const url = `${base}/analytics/export?${params.toString()}`;
      const resposta = await fetch(url, {
        headers: { authorization: `Bearer ${tokens.access ?? ""}` },
      });

      if (!resposta.ok) {
        toast.error("Não foi possível exportar");
        return;
      }

      const blob = await resposta.blob();
      const nome =
        resposta.headers.get("content-disposition")?.match(/filename="(.+)"/)?.[1] ??
        `${entity}.${format}`;

      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = nome;
      link.click();
      URL.revokeObjectURL(link.href);
    } finally {
      setBaixando(false);
    }
  };

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <div className="min-w-0">
          <CardTitle>Exportar planilha</CardTitle>
          <CardDescription>
            Sai com o período escolhido no topo e respeita o seu acesso: campo marcado como sensível
            não sai para quem não o vê na tela.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap items-end gap-3">
        <div className="min-w-40 flex-1 space-y-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            O que exportar
          </span>
          <Select
            value={entity}
            onChange={(e) => setEntity(e.target.value as typeof entity)}
            aria-label="O que exportar"
          >
            {EXPORT_ENTITIES.map((e) => (
              <option key={e} value={e}>
                {EXPORT_ENTITY_LABEL[e]}
              </option>
            ))}
          </Select>
        </div>
        <div className="w-32 space-y-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Formato
          </span>
          <Select
            value={format}
            onChange={(e) => setFormat(e.target.value as "csv" | "xlsx")}
            aria-label="Formato"
          >
            <option value="xlsx">XLSX</option>
            <option value="csv">CSV</option>
          </Select>
        </div>
        <Button onClick={() => void baixar()} disabled={baixando}>
          <Download />
          {baixando ? "Gerando…" : "Baixar"}
        </Button>
      </CardContent>
    </Card>
  );
}
